import React from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { FiltersState } from '../../hooks/useFilters';
import { useAuthStore } from '../../store/authStore';
import {
  normalizeSearchSource,
  resolveSearchSourceConnectionStatus,
  type SearchSourceConnectionStatus,
} from '../utils/sourceConnectionStatus';
import { getActiveFiltersCount } from '../../utils/filterUtils';

interface SearchInfoCardProps {
  filters: FiltersState;
  searchData: any;
  loading: boolean;
  onOpenAdmin?: () => void;
  setViewMode?: (mode: "search" | "admin-panel" | "hh-settings" | "statistics" | "activity" | "hh-auth" | "ats-settings" | "vacancies") => void;
}

const SOURCE_NAMES: Record<string, string> = {
  hh: 'HeadHunter',
  estaff: 'E-Staff',
  skillaz: 'Skillaz',
  avito: 'Авито',
};

const SearchInfoCard: React.FC<SearchInfoCardProps> = ({
  filters,
  searchData,
  loading,
  onOpenAdmin,
  setViewMode
}) => {
  // Статус готовности выбранного источника и обязательной ATS.
  const normalizedSource = normalizeSearchSource(filters.source);
  const isHHSource = normalizedSource === 'hh';
  const [sourceStatus, setSourceStatus] = React.useState<SearchSourceConnectionStatus | null>(null);

  React.useEffect(() => {
    const { isAuth, isLoading: authLoading } = useAuthStore.getState();
    if (authLoading || !isAuth) {
      setSourceStatus(null);
      return;
    }

    let cancelled = false;
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    const fetchStatus = async (attempt: number = 1) => {
      try {
        const status = await resolveSearchSourceConnectionStatus(normalizedSource);
        if (!cancelled) setSourceStatus(status);
      } catch (error: any) {
        console.error('Error fetching source status:', error);
        if (!cancelled) setSourceStatus('unavailable');
        if (!cancelled && attempt < 2) {
          await delay(400);
          if (!cancelled) await fetchStatus(attempt + 1);
        }
      }
    };
    setSourceStatus(null);
    void fetchStatus();
    return () => { cancelled = true; };
  }, [normalizedSource]);

  const sourceStatusTone =
    sourceStatus === 'connected'
      ? 'green'
      : sourceStatus === 'auth-required' || sourceStatus === 'ats-required'
        ? 'yellow'
        : 'gray';

  const sourceStatusText = (() => {
    if (sourceStatus === 'connected') return isHHSource ? 'HH.ru подключен' : 'ATS подключена';
    if (sourceStatus === 'auth-required') return 'Требуется авторизация';
    if (sourceStatus === 'ats-required') return 'Требуется настройка ATS';
    return 'Статус недоступен';
  })();

  const sourceActionText = (() => {
    if (sourceStatus === 'auth-required' && isHHSource) return 'Авторизоваться в HH.ru';
    if (sourceStatus === 'ats-required') return 'Настроить ATS';
    if (isHHSource) return 'Настройки HH.ru';
    return sourceStatus === 'connected' ? 'Настройки ATS' : 'Настроить ATS';
  })();

  const handleSourceSettingsClick = () => {
    if (!setViewMode) return;
    if (sourceStatus === 'auth-required' && isHHSource) {
      setViewMode('hh-auth');
      return;
    }
    if (sourceStatus === 'ats-required' || !isHHSource) {
      setViewMode('ats-settings');
      return;
    }
    setViewMode('hh-auth');
  };

  const activeFiltersCount = getActiveFiltersCount(filters);

  return (
    <div className="bg-[#f5f6f1] box-border content-stretch flex flex-col gap-6 items-start justify-start p-[12px] relative rounded-xl shrink-0 w-full">
      {/* Header with Title and Controls */}
      <div className="box-border content-stretch flex flex-row items-center justify-between gap-6 p-0 relative shrink-0 w-full">
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative text-[#000000] text-[32px] text-left flex-1 min-w-0">
          <p className="block leading-[40px] line-clamp-2 whitespace-normal break-words">
            Поиск из источников
          </p>
        </div>

        <div className="flex items-center gap-2">
          {sourceStatus ? (
            <>
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${
                  sourceStatusTone === 'green'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : sourceStatusTone === 'yellow'
                      ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                      : 'bg-gray-50 text-gray-600 border border-gray-200'
                }`}
              >
                {sourceStatus === 'connected' ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4" />
                    <span>{sourceStatusText}</span>
                  </>
                ) : sourceStatus === 'auth-required' || sourceStatus === 'ats-required' ? (
                  <>
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>{sourceStatusText}</span>
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>{sourceStatusText}</span>
                  </>
                )}
              </div>
              {setViewMode && (
                <button
                  onClick={handleSourceSettingsClick}
                  className={`px-4 py-2 text-sm font-medium rounded-3xl focus:outline-none ${
                    sourceStatusTone === 'green'
                      ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                      : sourceStatusTone === 'yellow'
                        ? 'text-white bg-yellow-600 hover:bg-yellow-700'
                        : 'text-gray-700 bg-gray-100'
                  }`}
                >
                  {sourceActionText}
                </button>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm bg-gray-50 text-gray-600 border border-gray-200">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              <span>Проверка статуса...</span>
            </div>
          )}
        </div>
      </div>

      {/* Search Info */}
      <div className="bg-[#ffffff] relative rounded-xl shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-6 items-start justify-start p-0 relative w-full">
            <div className="box-border content-stretch flex flex-row items-center justify-start gap-8 p-[20px] relative shrink-0 w-full">
              <div className="box-border content-stretch flex flex-row font-['Inter:Regular',_sans-serif] font-normal gap-2 items-center justify-start leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px]">
                <div className="css-bbeeyw relative shrink-0 text-[#525866]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    Источник
                  </p>
                </div>
                <div className="css-8vheua relative shrink-0 text-[#000000]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    {SOURCE_NAMES[filters.source] || filters.source}
                  </p>
                </div>
              </div>

              <div className="box-border content-stretch flex flex-row font-['Inter:Regular',_sans-serif] font-normal gap-2 items-center justify-start leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px]">
                <div className="css-bbeeyw relative shrink-0 text-[#525866]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    Результатов
                  </p>
                </div>
                <div className="css-8vheua relative shrink-0 text-[#000000]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    {(searchData?.count || 0).toLocaleString()}{searchData?.isApproximate ? ' (не точное)' : ''}
                  </p>
                </div>
              </div>

              <div className="box-border content-stretch flex flex-row font-['Inter:Regular',_sans-serif] font-normal gap-2 items-center justify-start leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px]">
                <div className="css-bbeeyw relative shrink-0 text-[#525866]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    Активных фильтров
                  </p>
                </div>
                <div className="css-8vheua relative shrink-0 text-[#000000]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    {activeFiltersCount}
                  </p>
                </div>
              </div>

              <div className="box-border content-stretch flex flex-row font-['Inter:Regular',_sans-serif] font-normal gap-2 items-center justify-start leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px]">
                <div className="css-bbeeyw relative shrink-0 text-[#525866]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    Страница
                  </p>
                </div>
                <div className="css-8vheua relative shrink-0 text-[#000000]">
                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                    {filters.page} из {Math.ceil((searchData?.count || 0) / filters.pageSize)}
                  </p>
                </div>
              </div>

              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchInfoCard;
